import { Box, Button, Flex, SxProps, Text, Tooltip } from '@modusign/design-system';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalContentProps } from '@app/features/apply-react-signing/components/AttachmentListModal';
import { getFormattedFilename } from '@app/features/apply-react-signing/libs/formatter/getFormattedFilename';

import DesktopAttachmentListItem from './DesktopAttachmentListItem';

export default function DesktopAttachmentList({
  attachments,
  enableDownload = true,
  isDocumentCompleted,
  isDownloading,
  onDownloadAttachmentBundleClick,
  onDownloadAttachmentClick,
  onOpenAttachmentClick,
}: ModalContentProps) {
  const { t } = useTranslation();
  const signerAttachments = attachments.filter((a) => a.participantReference.type === 'SIGNER');
  const requesterAttachments = attachments.filter(
    (a) => a.participantReference.type === 'REQUESTER'
  );

  return (
    <Flex flexDirection="column" maxHeight="384px" overflow="auto" width="100%">
      {!!requesterAttachments.length && (
        <Box as="table" sx={{ tableLayout: 'fixed' }} width="100%">
          <thead>
            <Box as="tr" borderBottom="1px solid" borderBottomColor="lightGray500">
              <AttachmentListTH sx={{ width: '128px' }}>
                {t('DocumentAttachmentListModal-requester')}
              </AttachmentListTH>
              {enableDownload && (
                <AttachmentListTH>
                  <Flex alignItems="center" justifyContent="space-between">
                    <span>{t('DocumentAttachmentListModal-attachedFile')}</span>
                    <Button
                      height="30px"
                      isLoading={isDownloading}
                      px="16px"
                      sx={{ position: 'relative' }}
                      variant="subtle"
                      onClick={() => onDownloadAttachmentBundleClick('REQUESTER')}
                    >
                      {t('DocumentAttachmentListModal-bundleDownload')}
                    </Button>
                  </Flex>
                </AttachmentListTH>
              )}
            </Box>
          </thead>
          <Box as="tbody">
            {requesterAttachments.map((item, index) => {
              return (
                <DesktopAttachmentListItem
                  enableDownload={enableDownload}
                  fileName={getFormattedFilename(item.file)}
                  key={item.id}
                  participantName={item.participantReference.name || ''}
                  onDownload={() => onDownloadAttachmentClick(item, index)}
                  onOpen={() => onOpenAttachmentClick(item, index)}
                />
              );
            })}
          </Box>
        </Box>
      )}
      {!!(signerAttachments.length && requesterAttachments.length) && (
        <Box
          bg="dividerNeutralLight"
          borderBottom="1px solid"
          borderBottomColor="lightGray500"
          height="12px"
        />
      )}
      {!!signerAttachments.length && (
        <Box as="table" sx={{ tableLayout: 'fixed' }} width="100%">
          <thead>
            <Box as="tr" borderBottom="1px solid" borderBottomColor="lightGray500">
              <AttachmentListTH sx={{ width: '128px' }}>
                {t('DocumentAttachmentListModal-signer')}
              </AttachmentListTH>
              <AttachmentListTH>
                <Flex alignItems="center" justifyContent="space-between">
                  <span>
                    {t('DocumentAttachmentListModal-reqeustedFile')} /
                    {t('DocumentAttachmentListModal-attachedFile')}
                  </span>
                  {enableDownload && (
                    <Tooltip
                      content={t('DocumentAttachmentListModal-bundleDownloadTooltip')}
                      disabled={isDocumentCompleted}
                      placement="left"
                    >
                      <Button
                        disabled={!isDocumentCompleted}
                        height="30px"
                        isLoading={isDownloading}
                        px="16px"
                        sx={{ position: 'relative' }}
                        variant="subtle"
                        onClick={() => onDownloadAttachmentBundleClick('SIGNER')}
                      >
                        {t('DocumentAttachmentListModal-bundleDownload')}
                      </Button>
                    </Tooltip>
                  )}
                </Flex>
              </AttachmentListTH>
            </Box>
          </thead>
          <Box as="tbody">
            {signerAttachments.map((item, index) => (
              <DesktopAttachmentListItem
                attachmentType={item.title}
                enableDownload={enableDownload}
                fileName={getFormattedFilename(item.file)}
                key={item.id}
                participantName={item.participantReference.name || ''}
                onDownload={() => onDownloadAttachmentClick(item, index)}
                onOpen={() => onOpenAttachmentClick(item, index)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Flex>
  );
}
interface AttachmentListTHProps extends SxProps {
  children: ReactNode;
}
function AttachmentListTH({ children, sx }: AttachmentListTHProps) {
  return (
    <Text
      as="th"
      color="textNeutralVeryLight"
      fontWeight="medium"
      px={7}
      py={6}
      sx={sx}
      textAlign="left"
    >
      {children}
    </Text>
  );
}
